import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { CartContext } from '../CartProvider';
import analytics from '../utils/analytics';
import { trackCartEvent } from '../utils/cartTelemetry';
import { reportMetaEvent } from '../utils/consentScripts';
import './Product.css';
import './Shop.css';
import { Helmet } from 'react-helmet';
import { ReviewBlock } from './ReviewBlock';

export function Product() {
  const [productDetail, setProductDetail] = useState(null);
  const { identifier } = useParams();
  const [itemCount, setItemCount] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, showDialog, setShowDialog, dispatch, setLatestAddedItem } = useContext(CartContext);
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [technicalDetailsOpen, setTechnicalDetailsOpen] = useState(false);
  const [similarProducts, setSimilarProducts] = useState(null);

  const schemaData = useMemo(() => {
    if (!productDetail) return null;
    return {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": productDetail.name,
      "image": productDetail.imagesList,
      "description": productDetail.description,
      "sku": productDetail.productNumber,
      "brand": {
        "@type": "Brand",
        "name": "ho.li design"
      },
      "offers": {
        "@type": "Offer",
        "url": productDetail.url,
        "priceCurrency": productDetail.currency,
        "price": productDetail.priceFloat,
        "priceValidUntil": productDetail.priceValidUntil,
        "itemCondition": "https://schema.org/NewCondition",
        "availability": productDetail.isInStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
      }
    };
  }, [productDetail]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [identifier]);

  useEffect(() => {
    // Fetch product
    fetch(`api/Product/${identifier}`)
      .then(response => {
        if (response.status === 404) {
          navigate('/404');
          return;
        }
        return response.json();
      })
      .then(data => {
        if (data) setProductDetail(data);
      });
  }, [identifier]);

  useEffect(() => {
    setSimilarProducts(null);
    setDescriptionOpen(false);
    setTechnicalDetailsOpen(false);
    fetch(`api/Product/${identifier}/similar`)
      .then(r => r.json())
      .then(data => setSimilarProducts(data));
  }, [identifier]);

  // Fire view_item when product details are loaded
  useEffect(() => {
    if (!productDetail) return;
    try {
      const price = productDetail.priceFloat || parseFloat(String(productDetail.price).replace(',', '.').replace(/[^0-9.-]+/g, '')) || 0;
      analytics.sendEvent('view_item', {
        item_id: productDetail.identifier || productDetail.productNumber,
        item_name: productDetail.title || productDetail.name || productDetail.metaTitle,
        price: price,
        currency: productDetail.currency || 'EUR'
      });
      reportMetaEvent('ViewContent', {
        content_ids: [productDetail.technicalName],
        content_name: productDetail.title || productDetail.name || productDetail.metaTitle,
        content_type: 'product',
        value: price,
        currency: productDetail.currency || 'EUR'
      });
    } catch (e) {
      // ignore analytics errors
    }
  }, [productDetail]);

  const handleCountChange = (e) => {
    setItemCount(e.target.value);
  };

  const handleAddToCart = () => {
    const unitPrice = productDetail.priceFloat || parseFloat(String(productDetail.price).replace(',', '.').replace(/[^0-9.-]+/g, '')) || 0;
    const addedQty = Number(itemCount) || 1;
    const newCart = cart.some(c => c.id === productDetail.identifier)
      ? cart.map(c => c.id === productDetail.identifier ? { id: c.id, quantity: Number(c.quantity) + addedQty } : { id: c.id, quantity: c.quantity })
      : [...cart.map(c => ({ id: c.id, quantity: c.quantity })), { id: productDetail.identifier, quantity: addedQty }];
    dispatch({ type: 'ADD_ITEM', payload: {
        id: productDetail.identifier,
        quantity: itemCount,
        unitPrice: unitPrice,
        technicalName: productDetail.technicalName
      }
    });
    setLatestAddedItem({
      product: productDetail,
      quantity: itemCount,
      totalPrice: parseFloat(productDetail.price.replace(',', '.').replace(/[^0-9.-]+/g,"")) * Number(itemCount)
    });
    trackCartEvent('add', [{ id: productDetail.identifier, quantity: addedQty }], newCart);
    setShowDialog(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Analytics: add_to_cart
    try {
      const unitPrice = productDetail.priceFloat || parseFloat(String(productDetail.price).replace(',', '.').replace(/[^0-9.-]+/g, '')) || 0;
      analytics.sendEvent('add_to_cart', {
        items: [
          {
            item_id: productDetail.identifier || productDetail.productNumber,
            item_name: productDetail.title || productDetail.name,
            price: unitPrice,
            quantity: Number(itemCount)
          }
        ],
        value: unitPrice * Number(itemCount),
        currency: productDetail.currency || 'EUR'
      });
      reportMetaEvent('AddToCart', {
        content_ids: [productDetail.technicalName],
        content_name: productDetail.title || productDetail.name,
        content_type: 'product',
        value: unitPrice * Number(itemCount),
        currency: productDetail.currency || 'EUR'
      });
    } catch (e) {
      // ignore
    }
  };

  const handleBackClick = () => {
    navigate('/shop', {
      state: location.state
    });
  };  

    // Utility function to strip HTML tags
  const stripHtml = (html) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  return (
    <div>
      {productDetail && <Helmet>
        <title>{productDetail.metaTitle}</title>
        <meta name="description" content={productDetail.metaDescription} />
        <meta name="language" content="de" />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={productDetail.metaTitle} />
        <meta property="og:description" content={productDetail.metaDescription} />
        <meta property="og:url" content={productDetail.canonicalUrl} />
        <meta property="og:site_name" content="ho.li design" />
        <meta property="og:locale" content="de_DE" />
        {productDetail.images && Object.keys(productDetail.images).length > 0 && (
          <meta property="og:image" content={Object.keys(productDetail.images)[0]} />
        )}
        {schemaData && (
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
        )}
        <link rel="canonical" href={productDetail.canonicalUrl} />
      </Helmet> }
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px', gap: '8px', cursor: 'pointer' }} onClick={handleBackClick}>
        <img src="/imgs/back.png" alt="Back" style={{ height: '16px', width: 'auto', opacity: 0.5 }} />
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#868686' }}>Zurück zur Auswahl</span>
      </div>
      {productDetail && 
        <h1 style={{ marginBottom: '40px' }}>{productDetail.title}</h1>
      }
      <div className="product-container">
        <div className="product-image">
          {productDetail && productDetail.images && 
            <Carousel key={identifier}>
              {Object.entries(productDetail.images).map(([url, alt], index) => (
                <div key={index}>
                  <img src={url} title={productDetail.name} alt={alt} />
                </div>
              ))}
            </Carousel>
          }
        </div>
        <div className="product-details">
          {productDetail && 
            <div className='product-details-text'>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', fontWeight: 500, color: '#5A5A5A', margin: '0 0 4px' }}>{productDetail.price}</p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: '#868686', margin: '0 0 20px' }}><i>{productDetail.vatAndShipping}</i></p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: '#B0ADA7', letterSpacing: '0.04em', margin: '0 0 12px' }}>Art.-Nr. {productDetail.productNumber}</p>
              {productDetail.availability && productDetail.availabilityColor && 
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ 
                    display: 'inline-block', 
                    height: '10px', 
                    width: '10px', 
                    borderRadius: '50%', 
                    backgroundColor: productDetail.availabilityColor,
                    marginRight: '10px',
                    flexShrink: 0
                  }}></span>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', margin: 0, color: '#5A5A5A' }}>{productDetail.availability}</p>
                </div>
              }
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: '#868686', margin: '0 0 8px' }}>{productDetail.deliveryTime}</p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: '#868686', margin: '0 0 24px' }}>30 Tage Rückgaberecht • Kostenlose Rücksendung</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <label htmlFor="qty-input" style={{ margin: 0, fontSize: '11px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#868686', whiteSpace: 'nowrap' }}>Menge</label>
                <input
                  id="qty-input"
                  type="number"
                  min="1"
                  value={itemCount}
                  onChange={handleCountChange}
                  style={{ width: '64px' }}
                />
              </div>
              <button className="global-button" onClick={handleAddToCart} disabled={!productDetail.isInStock} style={{ marginBottom: '20px' }}>In den Warenkorb</button>
            </div>
          }
        </div>
      </div>
      <hr style={{ border: 'none', borderTop: '1px solid #D4D2CB', margin: '8px 0 0' }} />
      <div className="collapsible-section">
        <button className="collapsible-header" onClick={() => setDescriptionOpen(o => !o)} aria-expanded={descriptionOpen}>
          <h2>Beschreibung</h2>
          <span className={`collapsible-chevron${descriptionOpen ? ' open' : ''}`}>&#8250;</span>
        </button>
        {descriptionOpen && productDetail && (
          <div className="collapsible-body">
            <p dangerouslySetInnerHTML={{ __html: productDetail.description }}></p>
          </div>
        )}
      </div>
      <hr style={{ border: 'none', borderTop: '1px solid #D4D2CB', margin: '0' }} />
      <div className="collapsible-section">
        <button className="collapsible-header" onClick={() => setTechnicalDetailsOpen(o => !o)} aria-expanded={technicalDetailsOpen}>
          <h2>Technische Details</h2>
          <span className={`collapsible-chevron${technicalDetailsOpen ? ' open' : ''}`}>&#8250;</span>
        </button>
        {technicalDetailsOpen && productDetail && (
          <div className="collapsible-body">
            <p dangerouslySetInnerHTML={{ __html: productDetail.technicalDetails }}></p>
          </div>
        )}
      </div>
      <hr style={{ border: 'none', borderTop: '1px solid #D4D2CB', margin: '0 0 48px' }} />

      {similarProducts && similarProducts.sameFabric && similarProducts.sameFabric.length > 0 && (
        <div className="similar-section">
          <p className="similar-section-title">Mich gibt es auch in anderen Größen und Farben</p>
          <div className="similar-products-row">
            {similarProducts.sameFabric.map(product => (
              <div key={product.technicalName} className="product-card" onClick={() => navigate(`/product/${product.technicalName}`)}>
                <div className="product-card-image-wrap">
                  <img src={product.image} alt={product.name} />
                </div>
                <div className="product-card-name">{product.name}</div>
                <div className="product-card-meta">
                  <span className="product-availability-dot" style={{ backgroundColor: product.availability }}></span>
                  <p className="product-card-price">{product.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {similarProducts && similarProducts.differentFabric && similarProducts.differentFabric.length > 0 && (
        <div className="similar-section">
          <p className="similar-section-title">Das könnte dir auch gefallen</p>
          <div className="similar-products-row">
            {similarProducts.differentFabric.map(product => (
              <div key={product.technicalName} className="product-card" onClick={() => navigate(`/product/${product.technicalName}`)}>
                <div className="product-card-image-wrap">
                  <img src={product.image} alt={product.name} />
                </div>
                <div className="product-card-name">{product.name}</div>
                <div className="product-card-meta">
                  <span className="product-availability-dot" style={{ backgroundColor: product.availability }}></span>
                  <p className="product-card-price">{product.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    <div>
      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#868686', margin: '0 0 0' }}>Das sagen unsere Kunden</p>
      <ReviewBlock />
    </div>
    </div>
  );
}
